import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useRecipe, RecipeData, RecipeVariant } from '../../context/RecipeContext';
import { useAuth } from '../../context/AuthContext';
import RecipeVariantForm from '../../components/RecipeVariantForm/RecipeVariantForm';
import Header from '../../components/Header';
import styles from './RecipeVariantFormScreen.module.css';

const RecipeVariantFormScreen: React.FC = () => {
  const navigate = useNavigate();
  const { recipeId, variantId } = useParams<{ recipeId?: string; variantId?: string }>();
  const location = useLocation();
  const { getRecipeById, getVariant } = useRecipe();
  const { user, login, logout } = useAuth();
  
  // Check if a recipe was passed through navigation state
  const passedRecipe = location.state?.recipe as RecipeData | undefined;
  
  const [baseRecipe, setBaseRecipe] = useState<RecipeData | undefined>(passedRecipe);
  const [existingVariant, setExistingVariant] = useState<RecipeVariant | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine the mode based on params
  const mode = variantId ? 'edit' : 'create';

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // If editing existing variant, load it
        if (variantId) {
          const variant = await getVariant(variantId);
          if (variant) {
            setExistingVariant(variant);
            // Also load the base recipe if we don't have it
            if (!baseRecipe) {
              const recipe = await getRecipeById(variant.base_recipe_id);
              setBaseRecipe(recipe || undefined);
            }
          } else {
            setError('Variant not found');
          }
        }
        // If creating new variant and no recipe passed, load by ID
        else if (recipeId && !baseRecipe) {
          const recipe = await getRecipeById(recipeId);
          if (recipe) {
            setBaseRecipe(recipe);
          } else {
            setError('Base recipe not found');
          }
        }
        // If no recipeId and no passed recipe, we have a problem
        else if (!recipeId && !baseRecipe) {
          setError('No base recipe provided');
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load recipe data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [recipeId, variantId, getRecipeById, getVariant, baseRecipe]);

  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!baseRecipe) return <div className={styles.error}>No base recipe available</div>;

  const handleLogin = () => {
    // TODO: Navigate to login page or show login modal
    console.log('Login clicked');
  };

  return (
    <div className={styles.pageWrapper}>
      <Header user={user} onLogin={handleLogin} onLogout={logout} />
      <RecipeVariantForm
        mode={mode}
        baseRecipe={baseRecipe}
        existingVariant={existingVariant}
        onClose={() => navigate('/')}
        onSuccess={(variant) => {
          // Navigate back to home for now (variant detail page coming soon)
          navigate('/');
        }}
      />
    </div>
  );
};

export default RecipeVariantFormScreen;